import { TwinEntity } from '@repo/backend-types';
import { ReactNode, useMemo } from 'react';
import { Box3 } from 'three';
import { Entity, TwinEntityType, AppMode } from '@repo/frontend-types';
import { convertCoordLocalPoint, getAreaOutlineShape, topLevelTypeFor } from '@repo/ui';

import { useModeContext, useTwinContext } from '@repo/front-end-state';
import { Box } from '../../behaviours/Box';
import { EditView } from '../../behaviours/EditView';
import { RunningTrackArea } from './RunningTrackArea';

/**
 * Area Component
 * For all areas
 */

interface Props {
  entity: Entity; // The backend twin data graphQL configuration needed to generate 3D components.
  mapDiv: HTMLDivElement; //Reference to the Mapbox map container element from React-Three-Map for event triggering css changes to the map cursor style.
  lineage: TwinEntity[]; // the acenstry of this entity, oldest first
  depth: number;
  children: ReactNode;
}

// TODO should selection be different for different types of area?
export const Area = ({ entity, mapDiv, depth, lineage, children }: Props) => {
  const { appMode } = useModeContext();
  const { hasFloorLevel, multifloor } = useTwinContext();

  const { centre, areaBlock } = useMemo(() => {
    if (entity.boundaries === undefined || entity.boundaries === null) {
      throw new Error(`Area entity with id ${entity.id} has no boundaries. We cannot render it`);
    }

    return getAreaOutlineShape(entity.boundaries.polygons, depth);
  }, [entity, depth]);

  const labelPosition = convertCoordLocalPoint(centre, 1.5);

  const scaledBoundingBox = useMemo(() => {
    // Vector3.multiplyScalar mutates in place. Clone first so we don't
    // corrupt the shared areaBlock.boundingBox — which any other mesh
    // that uses areaBlock directly (e.g. Tracked/NotTracked main fill)
    // reads when computing its world bbox.
    return new Box3(
      areaBlock.boundingBox!.min.clone().multiplyScalar(0.01),
      areaBlock.boundingBox!.max.clone().multiplyScalar(0.01),
    );
  }, [areaBlock]);

  // Check if this is the running track area (after all hooks are called)
  if (
    entity.bID === 'TFN-WMS-S026' ||
    (entity.name && entity.name.toLowerCase().includes('running track'))
  ) {
    return (
      <RunningTrackArea
        entity={entity}
        mapDiv={mapDiv}
        depth={depth}
        lineage={lineage}
        children={children}
      />
    );
  }

  const outlineScale = entity.bID && entity.bID.includes('TFN-WMZ-A010') ? 1.5 : 1.0;
  const sidewaysPosition = entity.bID && entity.bID.includes('TFN-WMZ-A010') ? -4 : 0;

  return (
    <group
      position={[0, depth / 4000, 0]}
      // Tag for flyToTarget's scene traversal — lets computeEntityPose read
      // the area's world-space bounding box (including its altitude in the
      // 3D scene) for accurate camera framing. Same pattern as Asset.tsx.
      userData={{ bID: entity.bID }}
    >
      {appMode === AppMode.EDIT ? (
        <EditView mapDiv={mapDiv} lineage={lineage} entity={entity} geometry={areaBlock} />
      ) : (
        <group scale={[outlineScale, 1, 1]} position={[sidewaysPosition, 0, 0]}>
          <Box
            mapDiv={mapDiv}
            lineage={lineage}
            entity={entity}
            geometry={areaBlock}
            entityType={TwinEntityType.ZONE}
            labelBoundingBox={scaledBoundingBox}
            labelPosition={labelPosition}
            topLevelType={topLevelTypeFor({ hasFloorLevel, multifloor })}
          />
        </group>
      )}
      {children}
    </group>
  );
};
